<template>
  <div class="animated fadeIn">
    <div v-if="initializing" class="text-center">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-row>
        <b-col v-if="showUserNameField && model.userName" :lg="userNameColSize" class="mb-3">
          <b-card
            header-bg-variant="primary"
            border-variant="primary"
            header-border-variant="primary"
            class="h-100 mb-0"
            :header="$t('account.userName')"
            body-class="d-flex align-items-center justify-content-center"
          >
            <h4 class="mb-0 font-weight-normal text-truncate">{{ model.userName }}</h4>
          </b-card>
        </b-col>

        <b-col v-if="model.email" :lg="emailColSize" class="mb-3">
          <b-card
            header-bg-variant="primary"
            border-variant="primary"
            header-border-variant="primary"
            class="mb-0 h-100"
            :header="$t('account.email')"
            body-class="d-flex align-items-center justify-content-center"
            footer-class="d-flex flex-wrap p-0 align-items-center justify-content-between bg-white p-1"
          >
            <h4 class="mb-0 font-weight-normal text-truncate">{{ model.email }}</h4>

            <template v-slot:footer>
              <div class="m-2">
                <font-awesome-icon
                  :icon="model.emailConfirmed ? 'check-circle' : 'times-circle'"
                  class="align-middle"
                  :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl, 'text-success': model.emailConfirmed, 'text-danger': !model.emailConfirmed}"
                />

                <span class="text-muted">
                  <template v-if="model.emailConfirmed">
                    {{ $t('account.approved') }}
                  </template>

                  <template v-else>
                    {{ $t('account.disapproved') }}
                  </template>
                </span>
              </div>

              <b-btn size="sm" :to="model.emailConfirmed ? './change-email' : './confirm-email'" class="m-2">
                <template v-if="model.emailConfirmed">
                  {{ $t('account.changeEmail') }}
                </template>

                <template v-else>
                  {{ $t('account.confirmEmail') }}
                </template>
              </b-btn>
            </template>
          </b-card>
        </b-col>

        <b-col v-if="model.phoneNumber" :lg="phoneNumberColSize" class="mb-3">
          <b-card
            header-bg-variant="primary"
            border-variant="primary"
            header-border-variant="primary"
            class="mb-0 h-100"
            :header="$t('account.phoneNumber')"
            body-class="d-flex align-items-center justify-content-center"
            footer-class="d-flex flex-wrap p-0 align-items-center justify-content-between bg-white p-1"
          >
            <h4 dir="ltr" class="mb-0 font-weight-normal text-truncate">+{{ model.phoneNumber }}</h4>

            <template v-slot:footer>
              <div class="m-2">
                <font-awesome-icon
                  :icon="model.phoneNumberConfirmed ? 'check-circle' : 'times-circle'"
                  class="align-middle"
                  :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl, 'text-success': model.phoneNumberConfirmed, 'text-danger': !model.phoneNumberConfirmed}"
                />
                <span class="text-muted">
                  <template v-if="model.phoneNumberConfirmed">
                    {{ $t('account.approved') }}
                  </template>

                  <template v-else>
                    {{ $t('account.disapproved') }}
                  </template>
                </span>
              </div>

              <b-btn size="sm" :to="model.phoneNumberConfirmed ? './change-phone-number' : './confirm-phone-number'" class="m-2">
                <template v-if="model.phoneNumberConfirmed">
                  {{ $t('account.changePhoneNumber') }}
                </template>

                <template v-else>
                  {{ $t('account.confirmPhoneNumber') }}
                </template>
              </b-btn>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <profile-buttons v-if="userStatus.emailConfirmed && userStatus.phoneNumberConfirmed"/>
    </template>
  </div>
</template>

<script>
import Breadcrumb from '@/mixins/breadcrumb'
import Settings from '@/mixins/settings/index'
import ProfileButtons from './ProfileButtons'

export default {
  name: 'Profile',
  components: {
    ProfileButtons
  },
  mixins: [Breadcrumb, Settings],
  data () {
    return {
      initializing: true,
      progressing: false,
      model: {
        userName: null,
        email: null,
        emailConfirmed: false,
        phoneNumber: null,
        phoneNumberConfirmed: false
      }
    }
  },
  computed: {
    userNameColSize () {
      let size = 12
      if (this.model.email && this.model.phoneNumber)
        size = 4
      else if (this.model.email || this.model.phoneNumber)
        size = 6
      return size
    },
    emailColSize () {
      let size = 12
      const hasUserName = this.showUserNameField && this.model.userName
      if (hasUserName && this.model.phoneNumber)
        size = 4
      else if (hasUserName || this.model.phoneNumber)
        size = 6
      return size
    },
    phoneNumberColSize () {
      let size = 12
      const hasUserName = this.showUserNameField && this.model.userName
      if (hasUserName && this.model.email)
        size = 4
      else if (hasUserName || this.model.email)
        size = 6
      return size
    },
    userStatus () {
      return this.$store.state.$userStatus
    }
  },
  mounted () {
    this.setBreadcrumb()
    this.getModel()
    .then(() => {
      this.getSettings(`${this.$config.auth.userType}Account`)
      .then(() => {
        this.initializing = false
      })
    })
    .catch(err => {
      this.fail(err)
      this.goto('/dashboard')
    })
  },
  methods: {
    setBreadcrumb () {
      const items = []
      let path = ''

      path += '/account'
      items.push({ path, text: this.$t('nav.account') })

      path += '/profile'
      items.push({ path, text: this.$t('nav.profile') })

      this.commitBreadcrumb(items)
    },
    getModel () {
      return this.$api.account.getProfile()
      .then(res => {
        this.model = res
        delete this.model.id
      })
    },
    fail (err) {
      this.$showError(this.$getLocaleErrorMessage(err, 'account'))
      this.progressing = false
      return false
    },
    goto (path) {
      if (path) this.$router.push({ path })
    }
  }
}
</script>
